import React, { useState } from 'react';
import {
  Database,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Users,
  Activity,
  Clock,
  Building2
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import Toast, { ToastType } from '@/components/ui/Toast';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { useSyncHistory } from '@/hooks/useSyncHistory';
import organizationService from '@/services/api/organizationService';

const IntegrationManagement: React.FC = () => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [showSyncDialog, setShowSyncDialog] = useState(false);

  const { history: syncHistory, lastSync, refetch: refetchHistory } = useSyncHistory({ limit: 10 });

  const lastSyncTime = lastSync?.sync_started_at
    ? new Date(lastSync.sync_started_at).toLocaleString()
    : null;

  const showToast = (message: string, type: ToastType) => {
    setToast({ message, type });
  };

  const handleSync = async () => {
    setShowSyncDialog(false);
    setIsSyncing(true);
    try {
      const results = await organizationService.syncFromFO();

      const empResult = results.employees;
      const posResult = results.positions;
      const totalSynced = empResult.successful_records + posResult.successful_records;

      showToast(
        `Successfully synced ${totalSynced} records (${empResult.successful_records} employees, ${posResult.successful_records} positions)`,
        'success'
      );

      // Reload history from database
      await refetchHistory();
    } catch (error) {
      console.error('Sync failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to sync data from F&O';
      showToast(
        errorMessage.includes('401') || errorMessage.includes('403')
          ? 'Authentication required to sync. Please log in.'
          : errorMessage,
        'error'
      );

      // Reload history (failure is also recorded in DB)
      await refetchHistory();
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">F&O Integration Management</h2>
          <p className="text-gray-600 mt-1">
            Synchronize team member data from Dynamics Finance & Operations
          </p>
        </div>
        <Button
          className="bg-heritage-blue hover:bg-heritage-blue/90"
          onClick={() => setShowSyncDialog(true)}
          disabled={isSyncing}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
          {isSyncing ? 'Syncing...' : 'Sync Now'}
        </Button>
      </div>

      {/* Integration Status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">F&O Connection</p>
              <p className="text-2xl font-bold text-emerald-600">Connected</p>
            </div>
            <div className="p-3 rounded-lg bg-emerald-100">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Last Sync</p>
              <p className="text-lg font-bold text-gray-900">{lastSyncTime || 'Never'}</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-100">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Last Synced</p>
              <p className="text-2xl font-bold text-gray-900">{lastSync?.records_processed ?? '-'}</p>
            </div>
            <div className="p-3 rounded-lg bg-purple-100">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Sync Status</p>
              <p className="text-2xl font-bold text-emerald-600">{isSyncing ? 'In Progress' : 'Ready'}</p>
            </div>
            <div className="p-3 rounded-lg bg-emerald-100">
              <Activity className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* F&O Integration Details */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Database className="w-5 h-5 text-heritage-blue" />
              Dynamics F&O Integration
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Team member data, organisational structure, and role synchronisation
            </p>
          </div>
        </div>

        {/* What gets synced */}
        <div className="space-y-3">
          <h4 className="font-semibold text-gray-900">Data Synchronized from F&O</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { label: 'Team Member Records', description: 'Names, emails, personnel IDs, departments' },
              { label: 'Organisational Structure', description: 'Campuses, departments, reporting lines' },
              { label: 'People Leader Relationships', description: 'Direct report assignments' },
              { label: 'Employment Status', description: 'Active/terminated status, hire dates' }
            ].map((item, idx) => (
              <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-900">{item.label}</p>
                <p className="text-xs text-gray-600 mt-1">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Sync info box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900">About F&O Synchronization</p>
              <p className="text-sm text-blue-800 mt-1">
                The sync process fetches all active team members from Dynamics F&O and updates the local database.
                New team members will be created, and existing team member records will be updated with the latest information.
                This process typically takes 2-3 minutes depending on the number of team members.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Sync History */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-heritage-blue" />
          Recent Sync History
        </h3>
        {syncHistory.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No sync history available</p>
            <p className="text-sm mt-1">Run a sync to see history here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {syncHistory.map((item) => (
              <div key={item.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-3">
                  {item.status === 'failed' ? (
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  ) : (
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {item.status === 'failed' ? 'Sync Failed' : 'Sync Completed'}
                      {item.entity_type !== 'employees' && (
                        <span className="text-gray-500 ml-1">({item.entity_type})</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-600">
                      {item.sync_started_at ? new Date(item.sync_started_at).toLocaleString() : '-'}
                      {item.triggered_by === 'scheduled' && ' (scheduled)'}
                    </p>
                  </div>
                </div>
                {item.status !== 'failed' && (
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">{item.records_processed} team members</p>
                    <p className="text-xs text-gray-600">
                      {item.records_created} new, {item.records_updated} updated
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Sync Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showSyncDialog}
        onClose={() => setShowSyncDialog(false)}
        onConfirm={handleSync}
        title="Sync F&O Data"
        message={`This will synchronize all team member data from Dynamics F&O. The process may take 2-3 minutes.

Do you want to proceed?`}
        confirmText="Start Sync"
        type="info"
      />
    </div>
  );
};

export default IntegrationManagement;
